import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readOrganization, updateOrganization } from '@/lib/api/generated/api';
import type {
  OrganizationView,
  UpdateOrganizationRequest,
} from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';
import { UsageError, isForbidden } from '@/features/workspace-usage';

import { organizationKeys } from '../queryKeys';

/**
 * The organization's own identity — its name and which deployment this is.
 *
 * Readable by anyone signed in; only an administrator may change it (spec 073 FR-021,
 * and the service's own split). That asymmetry is why this is a plain read with no
 * forbidden branch: a member who reaches it gets the name, and it is the *mutation*
 * below that refuses them.
 */
export function useOrganization() {
  return useQuery<OrganizationView>({
    queryKey: organizationKeys.detail(),
    queryFn: async ({ signal }) => {
      const response = await readOrganization({ signal });
      return expectStatus(
        response,
        200,
        'Could not read the organization’s details.',
      );
    },
    refetchOnWindowFocus: false,
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateOrganizationRequest) => {
      const response = await updateOrganization(request);
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only an organization administrator can change these details.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not save the organization’s details.',
      );
    },
    onSuccess: (organization) => {
      // Written straight into the cache rather than invalidated: the response *is* the
      // new state, so refetching would ask for something already in hand and leave the
      // form showing the old name until it arrived.
      queryClient.setQueryData(organizationKeys.detail(), organization);
    },
  });
}

export { isForbidden };
